import React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import { TextView, convertColor } from '../../components/Display';
import { postData } from '../../Api';

const EMPTY_REPLY = '{"type":"quill","document":{}}';

// Scoped styles: force RTL on the Quill editor/reader and give the modal a clean,
// minimalist chat look. Kept local so global forms are untouched.
// Force RTL and undo Quill's default per-editor scroll so message bubbles grow
// with their content. Borders/focus/dark come from the global styles in index.css.
const SCOPED_CSS = `
.taroff-support-view .ql-editor { direction: rtl; text-align: right; }
.taroff-support-view .composer .ql-editor { min-height: 120px; max-height: 220px; }
.taroff-support-view .ql-editor.ql-blank::before { left: auto; right: 15px; }
.taroff-support-view .bubble .ql-container { border: none; background: transparent; }
.taroff-support-view .bubble .ql-editor { padding: 0; min-height: 0; height: auto; overflow: visible; line-height: 1.9; }
`;

// Read-only render of a stored Quill value (JSON) or plain text, always RTL.
const RichContent = ({ value }) => {
    if (!value) return <span className="text-gray-400">—</span>;
    let doc = null;
    if (typeof value === 'string' && value.trim().startsWith('{')) {
        try {
            const parsed = JSON.parse(value);
            doc = parsed.document !== undefined ? parsed.document : parsed;
        } catch (e) {
            doc = null;
        }
    }
    if (doc !== null && typeof doc === 'object') {
        return <ReactQuill value={doc} readOnly={true} theme="bubble" modules={{ toolbar: false }} />;
    }
    return <div dir="rtl" className="whitespace-pre-wrap">{value}</div>;
};

const Badge = ({ color, children, icon }) => (
    <span
        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
        style={{ backgroundColor: convertColor(color) || '#6b7280' }}
    >
        {icon && <i className={icon} />}
        {children}
    </span>
);

const isEmptyReply = (text) => {
    try {
        const parsed = JSON.parse(text);
        return !parsed.document || !parsed.document.ops || parsed.document.ops.length === 0;
    } catch (e) {
        return !text || !text.trim();
    }
};

export default function TaroffSupportRequestView(props) {
    const navigate = useNavigate();
    const id = props['support-request-id'];

    const [ticket, setTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [states, setStates] = useState([]);
    const [replyText, setReplyText] = useState(EMPTY_REPLY);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const threadEndRef = useRef(null);

    const loadData = async (scroll = false) => {
        try {
            const response = await postData('/api/panel/page/taroff-support-request-view/data', { id });
            if (response.status === 'success') {
                setTicket(response.data.ticket);
                setMessages(response.data.messages || []);
                setStates(response.data.states || []);
            } else {
                setError(response.message || 'خطا در بارگذاری اطلاعات درخواست');
            }
        } catch (e) {
            setError('خطا در برقراری ارتباط با سرور');
        } finally {
            setLoading(false);
            if (scroll) setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
        }
    };

    useEffect(() => {
        loadData(true);
    }, []);

    const handleSendReply = async () => {
        if (isEmptyReply(replyText) || sending) return;
        setSending(true);
        try {
            const response = await postData('/api/panel/page/taroff-support-request-reply/save', { id, text: replyText });
            if (response.status === 'success') {
                setReplyText(EMPTY_REPLY);
                await loadData(true);
            } else {
                setError(response.message || 'خطا در ارسال پاسخ');
            }
        } finally {
            setSending(false);
        }
    };

    const handleSetState = async (key) => {
        if (!ticket || ticket.state_id_key === key) return;
        const response = await postData('/api/panel/page/taroff-support-request-state-by-key/save', { id, key });
        if (response.status === 'success') {
            loadData();
        } else {
            setError(response.message || 'خطا در تغییر وضعیت');
        }
    };

    const handleCancel = () => {
        props.onDismiss ? props.onDismiss() : navigate(-1);
    };

    if (loading) {
        return (
            <div dir="rtl" className="flex items-center justify-center h-64 text-gray-500">
                <i className="fas fa-spinner fa-spin fa-lg ml-2" /> در حال بارگذاری...
            </div>
        );
    }

    if (error && !ticket) {
        return (
            <div dir="rtl" className="p-6 text-center">
                <div className="text-red-600 mb-4">{error}</div>
                <button onClick={handleCancel} className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-zinc-700">بازگشت</button>
            </div>
        );
    }

    const isClosed = ticket.state_id_key === 'Closed';

    // The conversation thread: original request first, then each reply. In the admin
    // panel "support" is us (aligned right/start), the shop is the counterpart (left).
    const thread = [
        { author: ticket.shop_id_title || 'فروشگاه', mine: false, time: ticket.insert_time, content: ticket.description, isRequest: true },
        ...messages.map((m) => ({
            author: m.user_id ? (ticket.shop_id_title || 'فروشگاه') : 'پشتیبانی',
            mine: !m.user_id,
            time: m.insert_time,
            content: m.text,
        })),
    ];

    return (
        <div dir="rtl" className="taroff-support-view flex flex-col h-[85vh] font-iransans text-gray-900 dark:text-gray-100">
            <style>{SCOPED_CSS}</style>

            {/* Header */}
            <div className="shrink-0 px-5 py-3.5 border-b border-gray-200 dark:border-zinc-700 flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h2 className="text-base font-bold truncate">{ticket.title}</h2>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                        <span className="font-mono">#{ticket.id}</span>
                        <span className="text-gray-300">|</span>
                        <Badge color={ticket.shop_id_color} icon="far fa-store">{ticket.shop_id_title || 'بدون فروشگاه'}</Badge>
                        <Badge color={ticket.type_id_color}>{ticket.type_id_title}</Badge>
                        <span className="text-gray-400"><TextView format={'persian-date-normal'}>{ticket.insert_time}</TextView></span>
                    </div>
                </div>
                <button onClick={handleCancel} className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 dark:hover:bg-zinc-700 transition-colors" title="بستن">
                    <i className="fas fa-xmark fa-lg" />
                </button>
            </div>

            {/* Quick status bar */}
            <div className="shrink-0 px-5 py-2.5 border-b border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-900/60 flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500 ml-1">وضعیت:</span>
                {states.map((s) => {
                    const active = ticket.state_id_key === s.key;
                    return (
                        <button
                            key={s.id}
                            onClick={() => handleSetState(s.key)}
                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                                active
                                    ? 'text-white border-transparent shadow-sm'
                                    : 'bg-white dark:bg-zinc-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-zinc-600 hover:border-gray-400'
                            }`}
                            style={active ? { backgroundColor: convertColor(s.color) || '#4f46e5' } : {}}
                        >
                            {active && <i className="fas fa-check ml-1 text-[10px]" />}
                            {s.title}
                        </button>
                    );
                })}
            </div>

            {/* Conversation thread */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-gray-100/60 dark:bg-black/20">
                {thread.map((item, index) => (
                    <div key={index} className={`flex ${item.mine ? 'justify-start' : 'justify-end'}`}>
                        <div className={`bubble max-w-[82%] rounded-2xl px-4 py-2.5 border shadow-sm ${
                            item.mine
                                ? 'bg-indigo-50 border-indigo-100 dark:bg-indigo-950/40 dark:border-indigo-900/60'
                                : item.isRequest
                                    ? 'bg-amber-50 border-amber-100 dark:bg-amber-950/30 dark:border-amber-900/50'
                                    : 'bg-white border-gray-200 dark:bg-zinc-800 dark:border-zinc-700'
                        }`}>
                            <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs font-bold ${item.mine ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-300'}`}>
                                    <i className={`${item.mine ? 'far fa-headset' : 'far fa-store'} ml-1`} />
                                    {item.author}
                                </span>
                                {item.isRequest && <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">درخواست اولیه</span>}
                                <span className="text-[11px] text-gray-400 mr-auto"><TextView format={'persian-date-time'}>{item.time}</TextView></span>
                            </div>
                            <div className="text-sm"><RichContent value={item.content} /></div>
                        </div>
                    </div>
                ))}
                <div ref={threadEndRef} />
            </div>

            {/* Composer */}
            <div className="shrink-0 border-t border-gray-200 dark:border-zinc-700 px-4 py-3 bg-white dark:bg-zinc-900">
                {error && <div className="text-red-600 text-xs mb-2">{error}</div>}
                <div className="composer">
                    <ReactQuill theme="snow" value={(() => { try { return JSON.parse(replyText).document; } catch (e) { return replyText; } })()}
                        onChange={(content, delta, source, editor) => setReplyText(JSON.stringify({ type: 'quill', document: editor.getContents() }))}
                        placeholder="پاسخ خود را بنویسید..." />
                </div>
                <div className="mt-2.5 flex items-center justify-between">
                    <span className="text-xs text-gray-400">{isClosed ? 'این درخواست بسته است — پاسخ آن را دوباره باز می‌کند' : ''}</span>
                    <button
                        onClick={handleSendReply}
                        disabled={isEmptyReply(replyText) || sending}
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        <i className={sending ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'} />
                        ارسال پاسخ
                    </button>
                </div>
            </div>
        </div>
    );
}
